import { FC, useEffect, useRef } from 'react';
import {
  styled,
  Box,
  Button,
  CircularProgress,
  FormHelperText,
} from '@mui/material';
import { PhotoOutlined } from '@mui/icons-material';
import { Control, Controller, useWatch } from 'react-hook-form';
import { DataRoomFormData } from '../formData';
import { UploadType } from 'further-types/files';
import {
  getMaxSizeForUploadType,
  getValidFileTypesForUploadType,
} from 'further-ui/config';
import { Column, Row } from 'further-ui/components/Layout';
import { useNotification } from 'hooks/ui/useNotification';
import useFileUpload from 'hooks/ui/useFileUpload';

const PreviewBox = styled(Box)(({ theme }) => ({
  width: 40,
  height: 40,
  flexShrink: 0,
  cursor: 'pointer',
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.grey[200],
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: `1px solid ${theme.palette.grey[300]}`,
  overflow: 'hidden',
}));

const PreviewImage = styled('img')({
  width: '100%',
  height: '100%',
  objectFit: 'cover',
});

type Props = {
  control: Control<DataRoomFormData>;
};

const ContactImageUpload: FC<Props> = ({ control }) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const blobUrlRef = useRef<string | null>(null);
  const notification = useNotification();
  const { loading: isUploading, uploadFile } = useFileUpload();

  useEffect(() => {
    return () => {
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
      }
    };
  }, []);

  const acceptedFileTypes = getValidFileTypesForUploadType(
    UploadType.DataRoomDocuments,
  ).join(',');

  const imageUri = useWatch({ control, name: 'contact.image' })?.uri;

  const handleImageUpload = (
    onChange: (
      value: { uri: string; objectKey: string; fileName: string } | null,
    ) => void,
    file?: File,
  ) => {
    if (!file) return;

    const maxSizeMb = getMaxSizeForUploadType(
      UploadType.DataRoomDocuments,
      'mb',
    );
    if (file.size / 1_000_000 > maxSizeMb) {
      notification.error(`File size must be less than ${maxSizeMb}MB`);
      return;
    }

    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
    }
    blobUrlRef.current = URL.createObjectURL(file);

    onChange({
      uri: blobUrlRef.current,
      objectKey: '',
      fileName: file.name,
    });

    const handleFileUpload = async (file: File) => {
      const { data, error } = await uploadFile(
        file,
        UploadType.DataRoomDocuments,
      );
      if (error) {
        notification.error(error.message);
        return;
      }
      if (!data) return;

      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }

      onChange({
        uri: data.url,
        objectKey: data.filePath,
        fileName: file.name,
      });
    };

    void handleFileUpload(file);
  };

  return (
    <Controller
      name="contact.image"
      control={control}
      render={({ field: { onChange }, fieldState: { error } }) => (
        <Column spacing="xxs">
          <input
            ref={inputRef}
            type="file"
            accept={acceptedFileTypes}
            hidden
            onChange={(event) =>
              handleImageUpload(onChange, event.target.files?.[0])
            }
          />
          <Row spacing="sm" centered>
            <PreviewBox onClick={() => inputRef.current?.click()}>
              {isUploading ? (
                <CircularProgress size={16} color="info" />
              ) : imageUri ? (
                <PreviewImage src={imageUri} />
              ) : (
                <PhotoOutlined />
              )}
            </PreviewBox>
            <Button
              size="large"
              variant="contained"
              disabled={isUploading}
              onClick={() => inputRef.current?.click()}
            >
              Upload profile picture
            </Button>
          </Row>
          {error?.message && (
            <FormHelperText error>{error.message}</FormHelperText>
          )}
        </Column>
      )}
    />
  );
};

export default ContactImageUpload;
